import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './HiFiReview.css';
import ReviewComment from './ReviewComment';

const HiFiReviewList = ({ reviews = [], userId, onEditReview, onDeleteReview }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('date');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [manufacturerFilter, setManufacturerFilter] = useState(''); // Neuer Hersteller-Filter
  const [categories, setCategories] = useState([]);
  const [expandedComments, setExpandedComments] = useState({});
  const [reviewComments, setReviewComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [editCommentRating, setEditCommentRating] = useState(0);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const reviewsPerPage = 5;
  
  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('/api/marketplace/categories');
        if (!response.ok) {
          throw new Error('Failed to load categories');
        }
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    fetchCategories();
  }, []);
  
  // Fetch initial comment counts for all reviews
  useEffect(() => {
    if (!Array.isArray(reviews) || reviews.length === 0) return;
    
    const fetchCommentCounts = async () => {
      const counts = {};
      const loadingStates = {};
      
      for (const review of reviews) {
        loadingStates[review.id] = true;
        try {
          const response = await fetch(`/api/reviews/${review.id}/comments`);
          if (response.ok) {
            const data = await response.json();
            counts[review.id] = data.comments || [];
          }
        } catch (error) {
          console.error(`Error fetching comments for review ${review.id}:`, error);
        } finally {
          loadingStates[review.id] = false;
        }
      }
      
      setReviewComments(prev => ({ ...prev, ...counts }));
      setLoadingComments(prev => ({ ...prev, ...loadingStates }));
    };
    
    fetchCommentCounts();
  }, [reviews]);
  
  useEffect(() => {
    console.log("HiFiReviewList received userId:", userId);
    console.log("HiFiReviewList received reviews:", reviews);
  }, [userId, reviews]);
  
  // Ersetze die bestehende isUserReviewOwner Funktion mit dieser optimierten Version
  const isUserReviewOwner = (reviewUserId, currentUserId) => {
    // Debugging zur Fehlerfindung
    console.log(`Vergleiche: review.user_id=${reviewUserId} (${typeof reviewUserId}) mit userId=${currentUserId} (${typeof currentUserId})`);
    
    // Wenn einer der Werte fehlt, können wir nicht vergleichen
    if (reviewUserId === null || reviewUserId === undefined || 
        currentUserId === null || currentUserId === undefined) {
      return false;
    }
    
    // Versuche beide als Zahl zu parsen und zu vergleichen
    const reviewId = parseInt(String(reviewUserId).trim());
    const userId = parseInt(String(currentUserId).trim());
    
    // Strikt als Integer vergleichen
    const match = reviewId === userId && !isNaN(reviewId) && !isNaN(userId);
    console.log(`Vergleich nach Konvertierung: ${reviewId} === ${userId} = ${match}`);
    
    return match;
  };

  // Filter reviews by category
  const filteredByCategory = selectedCategory
    ? Array.isArray(reviews) 
      ? reviews.filter(review => 
          parseInt(review.category_id) === parseInt(selectedCategory) ||
          // Falls wir eine Hauptkategorie ausgewählt haben, zeige auch Unterkategorien
          (categories.some(cat => 
            cat.parent_id === parseInt(selectedCategory) && 
            parseInt(cat.id) === parseInt(review.category_id)
          ))
        )
      : []
    : reviews;
    
  // Weitere Filterlogik hinzufügen
  const filteredReviews = Array.isArray(filteredByCategory) 
    ? filteredByCategory.filter(review => {
        // Filter nach Hersteller wenn eingegeben
        if (manufacturerFilter && review.manufacturer) {
          return review.manufacturer.toLowerCase().includes(manufacturerFilter.toLowerCase());
        }
        return true;
      })
    : [];
    
  // Apply sorting - Fix by ensuring reviews is iterable
  const sortedReviews = Array.isArray(filteredReviews) ? [...filteredReviews].sort((a, b) => {
    if (sortBy === 'rating') {
      return b.rating - a.rating;
    }
    // Default sort by date
    return new Date(b.created_at) - new Date(a.created_at);
  }) : [];
  
  // Get current reviews based on pagination
  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = sortedReviews.slice(indexOfFirstReview, indexOfLastReview);
  const totalPages = Math.ceil(sortedReviews.length / reviewsPerPage);
  
  // Bei Änderung des Filters zurück zur ersten Seite
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, sortBy, manufacturerFilter]);
  
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };
  
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('de-DE', options);
  };
  
  // Hilfsfunktion, um entsprechendes Kategorie-Icon zu erhalten
  const getCategoryIcon = (categoryName) => {
    if (!categoryName) return 'devices_other';
    
    // Extrahiere den Hauptkategorie-Namen (alles vor dem ›)
    const mainCategoryName = categoryName.split('›')[0].trim().toLowerCase();
    
    // Prüfe zuerst auf den Hauptkategorie-Namen
    if (mainCategoryName.includes('lautsprecher')) return 'speaker';
    if (mainCategoryName.includes('verstärker') || mainCategoryName.includes('verstarker')) return 'equalizer';
    if (mainCategoryName.includes('plattenspieler') || mainCategoryName.includes('vinyl')) return 'album';
    if (mainCategoryName.includes('cd')) return 'music_note';
    if (mainCategoryName.includes('netzwerk') || mainCategoryName.includes('streamer')) return 'router';
    if (mainCategoryName.includes('kopfhörer') || mainCategoryName.includes('kopfhorer')) return 'headphones';
    if (mainCategoryName.includes('kabel')) return 'cable';
    if (mainCategoryName.includes('zubehör') || mainCategoryName.includes('zubehor')) return 'home_repair_service';
    
    // Fallback-Prüfung auf den vollständigen Namen (inkl. Unterkategorie)
    const nameLC = categoryName.toLowerCase();
    if (nameLC.includes('lautsprecher')) return 'speaker';
    if (nameLC.includes('verstärker') || nameLC.includes('verstarker')) return 'equalizer';
    if (nameLC.includes('plattenspieler') || nameLC.includes('vinyl')) return 'album';
    if (nameLC.includes('cd')) return 'music_note';
    if (nameLC.includes('netzwerk') || nameLC.includes('streamer')) return 'router';
    if (nameLC.includes('kopfhörer') || nameLC.includes('kopfhorer')) return 'headphones';
    if (nameLC.includes('kabel')) return 'cable';
    if (nameLC.includes('zubehör') || nameLC.includes('zubehor')) return 'home_repair_service';
    
    // Default icon
    return 'devices_other';
  };
  
  const toggleComments = async (reviewId) => {
    // Toggle the expanded state
    setExpandedComments(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId]
    }));
    
    // If we're expanding and don't have comments yet, fetch them
    if (!expandedComments[reviewId] && !reviewComments[reviewId]) {
      setLoadingComments(prev => ({ ...prev, [reviewId]: true }));
      
      try {
        const response = await fetch(`/api/reviews/${reviewId}/comments`);
        
        if (!response.ok) {
          throw new Error('Fehler beim Laden der Kommentare');
        }
        
        const data = await response.json();
        setReviewComments(prev => ({
          ...prev,
          [reviewId]: data.comments || []
        }));
      } catch (error) {
        console.error(`Error fetching comments for review ${reviewId}:`, error);
      } finally {
        setLoadingComments(prev => ({ ...prev, [reviewId]: false }));
      }
    }
  };
  
  const handleCommentAdded = (reviewId, newComment) => {
    setReviewComments(prev => ({
      ...prev,
      [reviewId]: [...(prev[reviewId] || []), newComment]
    }));
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment.id);
    setEditCommentText(comment.comment);
    setEditCommentRating(comment.rating);
  };

  const cancelEditComment = () => {
    setEditingComment(null);
    setEditCommentText('');
    setEditCommentRating(0);
  };

  const saveEditComment = async (reviewId, commentId) => {
    if (!editCommentText.trim()) {
      toast.error('Kommentar darf nicht leer sein.');
      return;
    }
    
    if (editCommentRating < 1) {
      toast.error('Bitte wählen Sie eine Bewertung aus.');
      return;
    }
    
    setIsSubmittingEdit(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Sie müssen angemeldet sein.');
      }
      
      // Make the actual API call to update the comment
      const response = await fetch(`/api/reviews/${reviewId}/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          comment: editCommentText,
          rating: editCommentRating
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Fehler beim Aktualisieren des Kommentars');
      }
      
      const updatedComment = await response.json();
      
      // Update state with the server response
      setReviewComments(prev => ({
        ...prev,
        [reviewId]: prev[reviewId].map(comment => 
          comment.id === commentId ? updatedComment : comment
        )
      }));
      
      setEditingComment(null);
      toast.success('Kommentar aktualisiert');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  // Also update the deleteComment function to ensure correct types
  const deleteComment = async (reviewId, commentId) => {
    if (!window.confirm('Möchten Sie diesen Kommentar wirklich löschen?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Sie müssen angemeldet sein.');
      }
      
      // Make the actual API call to delete the comment
      const response = await fetch(`/api/reviews/${reviewId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Löschen des Kommentars');
      }
      
      // Update state by removing the deleted comment
      setReviewComments(prev => ({
        ...prev,
        [reviewId]: prev[reviewId].filter(comment => parseInt(comment.id) !== parseInt(commentId))
      }));
      
      toast.success('Kommentar gelöscht');
    } catch (error) {
      toast.error(error.message);
      console.error('Delete comment error:', error);
    }
  };

  // Replace the existing isCurrentUserComment function with this fixed version
  const isCurrentUserComment = (comment) => {
    try {
      // Try to get the user ID from various storage options
      let currentUserId = null;
      
      // First try direct userId storage
      const directId = localStorage.getItem('userId');
      if (directId) {
        currentUserId = parseInt(directId);
      }
      
      // Then check the 'user' object in localStorage if userId not found
      if (!currentUserId) {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const userData = JSON.parse(userStr);
            if (userData && userData.id) {
              currentUserId = parseInt(userData.id);
            }
          } catch (e) {
            console.error('Error parsing user data:', e);
          }
        }
      }

      // Debug output
      console.log(`Comment check: user=${currentUserId}, comment.user_id=${comment.user_id}, types=${typeof currentUserId}/${typeof comment.user_id}`);
      
      // Make sure we have both IDs and ensure consistent integer comparison
      if (currentUserId && comment.user_id) {
        return parseInt(currentUserId) === parseInt(comment.user_id);
      }
      
      return false;
    } catch (e) {
      console.error('Error checking comment ownership:', e);
      return false;
    }
  };

  if (!Array.isArray(reviews) || reviews.length === 0) {
    return (
      <div className="review-list">
        <div className="review-list-header">
          <h2>
            <span className="material-symbols-outlined">reviews</span>
            Bewertungen
          </h2>
        </div>
        <div style={{ padding: '30px', textAlign: 'center', color: '#666' }}>
          <p>Noch keine Bewertungen vorhanden. Sei der Erste!</p>
        </div>
      </div>
    );
  }
  
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <button
          key={i}
          className={`page-button ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    return (
      <div className="pagination">
        <button 
          className="page-button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <span className="material-symbols-outlined">chevron_left</span>
        </button>
        
        {pageNumbers}
        
        <button 
          className="page-button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <span className="material-symbols-outlined">chevron_right</span>
        </button>
      </div>
    );
  };
  
  // Finde alle eindeutigen Kategorien in den Bewertungen
  const uniqueCategories = reviews
    .filter(review => review.category_name)
    .map(review => ({ 
      id: review.category_id,
      name: review.category_name 
    }))
    .filter((cat, index, self) => 
      self.findIndex(c => c.id === cat.id) === index
    );
  
  return (
    <div className="review-list">
      <div className="review-list-header">
        <h2>
          <span className="material-symbols-outlined">reviews</span>
          Bewertungen
        </h2>
      </div>

      <div className="review-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="category-filter">Kategorie:</label>
            <select
              id="category-filter"
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)}
              className="form-control"
            >
              <option value="">Alle Kategorien</option>
              {/* Hauptkategorien */}
              {categories
                .filter(cat => !cat.parent_id)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="sort-select">Sortieren nach:</label>
            <select 
              id="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="form-control"
            >
              <option value="date">Neueste zuerst</option>
              <option value="rating">Beste Bewertung</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="manufacturer-filter">Hersteller:</label>
            <input
              id="manufacturer-filter"
              type="text"
              value={manufacturerFilter}
              onChange={(e) => setManufacturerFilter(e.target.value)}
              placeholder="Hersteller suchen"
              className="form-control"
            />
          </div>
          
          {selectedCategory && (
            <button 
              onClick={() => setSelectedCategory(null)}
              className="reset-filter-button"
            >
              <span className="material-symbols-outlined">restart_alt</span>
              Filter zurücksetzen
            </button>
          )}
        </div>
      </div>
      
      {currentReviews.length === 0 ? (
        <div style={{ padding: '30px', textAlign: 'center', color: '#666' }}>
          <p>Keine Bewertungen für diese Auswahl gefunden.</p>
        </div>
      ) : (
        currentReviews.map((review) => (
          <div key={review.id} className="review-item">
            <div className="review-header-row">
              <div className="review-component">
                <span className="material-symbols-outlined">
                  {getCategoryIcon(review.category_name)}
                </span>
                {review.component}
              </div>
              <div className="review-rating">
                <div className="review-stars">
                  {[...Array(5)].map((_, i) => (
                    <span 
                      key={i} 
                      className="material-symbols-outlined"
                      style={{ color: i < review.rating ? 'gold' : '#ccc' }}
                    >
                      star
                    </span>
                  ))}
                </div>
                {review.is_recommended !== null && (
                  <div className={`review-recommendation ${review.is_recommended ? '' : 'negative'}`}>
                    <span className="material-symbols-outlined">
                      {review.is_recommended ? 'thumb_up' : 'thumb_down'}
                    </span>
                  </div>
                )}
                {review.price_performance && (
                  <div className={`price-performance-badge ${review.price_performance.toLowerCase()}`}>
                    <span className="label">Preis-/Leistung:</span>
                    <span className="material-symbols-outlined">
                      {review.price_performance === 'FAIR' ? 'payments' : 
                       review.price_performance === 'OKAY' ? 'balance' : 'trending_up'}
                    </span>
                    <span>{review.price_performance}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="review-meta">
              <div className="review-manufacturer">
                <span className="material-symbols-outlined">precision_manufacturing</span>
                <div className="manufacturer-info">
                  <span className="manufacturer-name">{review.manufacturer}</span>
                  <span className="category-name">{review.category_name}</span>
                </div>
              </div>
              <div className="review-date">
                <span className="material-symbols-outlined">event</span>
                {formatDate(review.created_at)}
              </div>
              <div className="review-user">
                <span className="material-symbols-outlined">person</span>
                <span className="username">{review.username}</span>
              </div>
            </div>
            
            {review.hifi_chain && (
              <div className="hifi-chain-section">
                <div className="chain-label">
                  <span className="material-symbols-outlined">settings_input_component</span>
                  HiFi-Kette:
                </div>
                <div className="chain-text">{review.hifi_chain}</div>
              </div>
            )}
            
            {review.review_text && (
              <div className="review-text-section">
                <div className="review-label">
                  <span className="material-symbols-outlined">rate_review</span>
                  Bewertung:
                </div>
                <div className="review-text">{review.review_text}</div>
              </div>
            )}
            
            {/* Debug-Ausgabe für jeden Review */}
            {console.log(`Review ${review.id}: user_id=${review.user_id} (${typeof review.user_id}), logged in userId=${userId} (${typeof userId}), match=${isUserReviewOwner(review.user_id, userId)}`)}

            {/* Comments section */}
            <div className="review-comments-section">
              <button 
                onClick={() => toggleComments(review.id)} 
                className="toggle-comments-button"
              >
                <span className="material-symbols-outlined">
                  {expandedComments[review.id] ? 'expand_less' : 'chat'}
                </span>
                {!expandedComments[review.id] ? (
                  reviewComments[review.id] && reviewComments[review.id].length > 0 ? 
                    `${reviewComments[review.id].length} Kommentare anzeigen` : 
                    'Kommentieren'
                ) : (
                  'Kommentare ausblenden'
                )}
              </button>
              
              {expandedComments[review.id] && (
                <div className="comments-container">
                  {loadingComments[review.id] ? (
                    <div className="loading-comments">
                      <div className="loading-spinner small"></div>
                      <span>Lade Kommentare...</span>
                    </div>
                  ) : (
                    <>
                      {reviewComments[review.id] && reviewComments[review.id].length > 0 ? (
                        <div className="comments-list">
                          {reviewComments[review.id].length > 2 && !expandedComments[`${review.id}-all`] && (
                            <div className="comments-preview">
                              {reviewComments[review.id].slice(0, 2).map(comment => (
                                <div key={comment.id} className="comment-item">
                                  <div className="comment-header">
                                    <div className="comment-user">
                                      <span className="material-symbols-outlined user-icon">account_circle</span>
                                      <span className="username">{comment.username}</span>
                                    </div>
                                    <div className="comment-date">
                                      {formatDate(comment.created_at)}
                                    </div>
                                    <div className="comment-rating">
                                      {[...Array(5)].map((_, i) => (
                                        <span 
                                          key={i} 
                                          className="material-symbols-outlined"
                                          style={{ color: i < comment.rating ? 'gold' : '#ccc', fontSize: '16px' }}
                                        >
                                          star
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="comment-text">{comment.comment}</div>
                                  {isCurrentUserComment(comment) && (
                                    <div className="comment-actions">
                                      <button 
                                        onClick={() => handleEditComment(comment)} 
                                        className="edit-comment-button"
                                      >
                                        <span className="material-symbols-outlined">edit</span>
                                      </button>
                                      <button 
                                        onClick={() => deleteComment(review.id, comment.id)} 
                                        className="delete-comment-button"
                                      >
                                        <span className="material-symbols-outlined">delete</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                              <button 
                                className="show-more-comments"
                                onClick={() => setExpandedComments(prev => ({
                                  ...prev,
                                  [`${review.id}-all`]: true
                                }))}
                              >
                                <span className="material-symbols-outlined">unfold_more</span>
                                Alle {reviewComments[review.id].length} Kommentare anzeigen
                              </button>
                            </div>
                          )}
                          
                          {(reviewComments[review.id].length <= 2 || expandedComments[`${review.id}-all`]) && (
                            <div className="comments-full">
                              {reviewComments[review.id].map(comment => (
                                <div key={comment.id} className="comment-item">
                                  {editingComment === comment.id ? (
                                    // Edit mode
                                    <div className="edit-comment-form">
                                      <div className="form-group rating-group">
                                        <label>Bewertung</label>
                                        <div className="stars-container">
                                          {[1, 2, 3, 4, 5].map(star => (
                                            <span
                                              key={star}
                                              className={`star material-symbols-outlined ${editCommentRating >= star ? 'filled' : ''}`}
                                              onClick={() => setEditCommentRating(star)}
                                            >
                                              star
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                      
                                      <div className="form-group">
                                        <textarea
                                          value={editCommentText}
                                          onChange={(e) => {
                                            if (e.target.value.length <= 800) {
                                              setEditCommentText(e.target.value);
                                            }
                                          }}
                                          rows="3"
                                          maxLength="800"
                                        />
                                        <div className="char-count">
                                          {editCommentText.length}/800
                                        </div>
                                      </div>
                                      
                                      <div className="edit-actions">
                                        <button 
                                          className="save-btn" 
                                          onClick={() => saveEditComment(review.id, comment.id)}
                                          disabled={isSubmittingEdit}
                                        >
                                          {isSubmittingEdit ? (
                                            <>
                                              <div className="loading-spinner small"></div>
                                              <span>Speichern...</span>
                                            </>
                                          ) : (
                                            <>
                                              <span className="material-symbols-outlined">save</span>
                                              <span>Speichern</span>
                                            </>
                                          )}
                                        </button>
                                        <button 
                                          className="cancel-btn" 
                                          onClick={cancelEditComment}
                                          disabled={isSubmittingEdit}
                                        >
                                          <span className="material-symbols-outlined">cancel</span>
                                          <span>Abbrechen</span>
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="comment-header">
                                        <div className="comment-user">
                                          <span className="material-symbols-outlined user-icon">account_circle</span>
                                          <span className="username">{comment.username}</span>
                                        </div>
                                        <div className="comment-date">
                                          {formatDate(comment.created_at)}
                                        </div>
                                        <div className="comment-rating">
                                          {[...Array(5)].map((_, i) => (
                                            <span 
                                              key={i} 
                                              className="material-symbols-outlined"
                                              style={{ color: i < comment.rating ? 'gold' : '#ccc', fontSize: '16px' }}
                                            >
                                              star
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                      <div className="comment-text">{comment.comment}</div>
                                      {isCurrentUserComment(comment) && (
                                        <div className="comment-actions">
                                          <button 
                                            onClick={() => handleEditComment(comment)} 
                                            className="edit-comment-button"
                                          >
                                            <span className="material-symbols-outlined">edit</span>
                                          </button>
                                          <button 
                                            onClick={() => deleteComment(review.id, comment.id)} 
                                            className="delete-comment-button"
                                          >
                                            <span className="material-symbols-outlined">delete</span>
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              ))}
                              
                              {reviewComments[review.id].length > 2 && (
                                <button 
                                  className="show-less-comments"
                                  onClick={() => setExpandedComments(prev => ({
                                    ...prev,
                                    [`${review.id}-all`]: false
                                  }))}
                                >
                                  <span className="material-symbols-outlined">unfold_less</span>
                                  Weniger anzeigen
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="no-comments">
                          <p>Noch keine Kommentare vorhanden.</p>
                        </div>
                      )}
                      
                      <ReviewComment 
                        reviewId={review.id} 
                        onCommentAdded={(newComment) => handleCommentAdded(review.id, newComment)} 
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Bearbeiten/Löschen-Buttons nur für eigene Reviews */}
            {isUserReviewOwner(review.user_id, userId) && (
              <div className="review-actions">
                <button 
                  onClick={() => onEditReview(review)} 
                  className="edit-review-button"
                  title="Bewertung bearbeiten"
                >
                  <span className="material-symbols-outlined">edit</span>
                  Bearbeiten
                </button>
                <button 
                  onClick={() => onDeleteReview(review.id)} 
                  className="delete-review-button"
                  title="Bewertung löschen"
                >
                  <span className="material-symbols-outlined">delete</span>
                  Löschen
                </button>
              </div>
            )}
          </div>
        ))
      )}
      
      {renderPagination()}
    </div>
  );
};

export default HiFiReviewList;